import { useEffect, useState } from "react";

import CustomColorPickerModal from "@/appComponents/Blocks/Composites/CompositeColorPicker/CustomColorPickerModal";
import CustomGradientPickerModal from "@/appComponents/Blocks/Composites/CompositeColorPicker/CustomGradientPickerModal";
import { areColorsEqual } from "@/appComponents/Blocks/Composites/CompositeColorPicker/ColorPicker.utils";
import SectionCard, {
  SectionCardGroup
} from "@/appComponents/Blocks//Composites/SectionCard/SectionCard";
import { BuildDateFormatOptions, BuildColorOptions } from "./ThemeConfigUtils";
import {
  Button,
  Checkbox,
  ColorPickerControl,
  Flex,
  Grid,
  Menu,
  Select,
  Text,
  Tooltip
} from "@theorgchartdev/storybook";
import { GetWSMgr } from "@/Utils/StateLib";
import { getLocalizedColorPickerStrings } from "@/Utils/Utils";
import { appstr } from "@coreapp/utils/appstr";
import { enumFeaturesList, enumSCArrowVariants } from "@coreapp/constants";
import { Globals } from "@coreapp/CoreApp/Globals";
import { getFeatureAccess } from "@/Utils/Misc";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import CopyPasteViewsSection from "../CopyPasteViewsSection/CopyPasteViewsSection";

const GetArrowName = arrow => {
  let name = appstr("Default");
  let selectedArrow = enumSCArrowVariants[arrow];
  switch (selectedArrow) {
    case enumSCArrowVariants.blackarrow:
      name = appstr("Black");
      break;
    case enumSCArrowVariants.whitearrow:
      name = appstr("White");
      break;
    case enumSCArrowVariants.themearrow:
      name = appstr("Theme");
      break;
    default:
      break;
  }
  return name;
};

const GetThemeCopy = themeObj => {
  return JSON.parse(JSON.stringify(themeObj));
};
export const ThemeConfiguration = ({ accountConfig, handleUpdate }) => {
  const baseClass = "ocn-theme";

  const [showCustomColor, setShowCustomColor] = useState(false); // false || 'standard' || 'gradient'

  const { isSupportLogin } = usePermissionCheck();

  const themeConfig = accountConfig.theme;
  if (!accountConfig) return;

  const wsMgr = GetWSMgr();
  const orgInfo = wsMgr?.fOrgInfo;
  let curDateFmt = themeConfig["dateformat"];
  if (curDateFmt == null) curDateFmt = orgInfo?.GetDateFormat();

  let curSCArrow = themeConfig["subchartarrow"];
  if (curSCArrow == null) curSCArrow = orgInfo.GetSCArrowStyle();

  let curTimeFormat = themeConfig["timeformat"];
  if (curTimeFormat == null) curTimeFormat = orgInfo.GetTimeFormat();

  let customColors = themeConfig["customcolors"];
  if (customColors == null) customColors = [];

  const customColorSwatches = BuildColorOptions(customColors);

  const eventTracker = wsMgr.GetUsageTracker();
  useEffect(() => {
    eventTracker.ReportEvent("account_settings.theme.opened");
  }, []);

  const SelectDateFormat = target => {
    let themeObj = GetThemeCopy(themeConfig);
    themeObj.dateformat = target.value;
    handleUpdate({ theme: themeObj }, "theme");
  };

  const ToggleTimeFormat = e => {
    let themeObj = GetThemeCopy(themeConfig);
    themeObj.timeformat = curTimeFormat === 24 ? 12 : 24;
    handleUpdate({ theme: themeObj }, "theme");
  };

  const AddCustColor = color => {
    let themeObj = GetThemeCopy(themeConfig);
    if (themeObj.customcolors == null) themeObj.customcolors = [];
    const includesColor = themeObj.customcolors.some(c => areColorsEqual(c, color));
    if (includesColor || themeObj.customcolors.length >= 20) {
      return;
    }
    themeObj.customcolors = [...themeObj.customcolors, color];
    handleUpdate({ theme: themeObj }, "theme");
  };

  const ClearAllCustomColors = () => {
    let themeObj = GetThemeCopy(themeConfig);
    themeObj.customcolors = [];
    handleUpdate({ theme: themeObj }, "theme");
  };

  const SelectSCArrow = arrow => {
    let themeObj = GetThemeCopy(themeConfig);
    themeObj.subchartarrow = enumSCArrowVariants[arrow];
    handleUpdate({ theme: themeObj }, "theme");
  };

  return (
    <SectionCardGroup>
      <SectionCard title={"Appearance"} isSingleCardNoGroup={true}>
        <Flex className="ocn-settings-section-wrapper" gap={"32px"} direction={"column"}>
          <Flex direction={"column"} gap={"8px"} width={"30%"}>
            <Text size="small" modifier={"medium"}>
              {appstr("Date Time Format")}
            </Text>
            <Flex gap={"12px"} width={"400px"} direction={"column"}>
              <Checkbox
                id={`${baseClass}-time-format`}
                data-testid="account-settings-theme-time-format-checkbox"
                label={appstr("Use 24 hour clock")}
                size="medium"
                checked={curTimeFormat === 24}
                onToggle={ToggleTimeFormat}
                disabled={!getFeatureAccess(enumFeaturesList.dateTimeFormat)}
              />
              <Select
                id={`${baseClass}-date-select`}
                dataTestId="account-settings-theme-date-format-select"
                value={curDateFmt}
                options={BuildDateFormatOptions(SelectDateFormat)}
                selectSize={"medium"}
                disabled={!getFeatureAccess(enumFeaturesList.dateTimeFormat)}
              />
            </Flex>
          </Flex>
          <Flex direction={"column"} gap={"8px"}>
            <Text size={"small"} modifier={"medium"}>
              {appstr("scarrowstyles")}
            </Text>
            <Flex gap={"24px"}>
              {Object.keys(enumSCArrowVariants).map((arrow, index) => {
                return (
                  <Checkbox
                    id={`${baseClass}-scarrow-${index}`}
                    data-testid={`account-settings-theme-arrow-style-${arrow}-radio`}
                    key={index}
                    label={GetArrowName(arrow)}
                    size={"medium"}
                    checked={curSCArrow === enumSCArrowVariants[arrow]}
                    onToggle={() => SelectSCArrow(arrow)}
                    disabled={!getFeatureAccess(enumFeaturesList.subchartArrowStyle)}
                  />
                );
              })}
            </Flex>
          </Flex>
          <Flex direction={"column"} gap={"12px"} alignItems="flex-start">
            <Text size={"small"} modifier={"medium"}>
              {appstr("Custom Colors")}
            </Text>
            <Flex gap="12px" alignItems="center">
              <Menu
                trigger={
                  <Button
                    modifier="primary"
                    size="small"
                    disabled={customColors.length >= 20}
                    iconProps={{ name: "plus-solid" }}
                    dataTestId="account-settings-theme-add-color-btn"
                  >
                    {appstr("Add")}
                  </Button>
                }
                options={[
                  {
                    label: appstr("Add custom solid color"),
                    value: "standard",
                    clickHandler: () => setShowCustomColor("standard"),
                    dataTestId: "account-settings-theme-add-solid-color-menu-item"
                  },
                  {
                    label: appstr("Add custom gradient"),
                    value: "gradient",
                    clickHandler: () => setShowCustomColor("gradient"),
                    dataTestId: "account-settings-theme-add-gradient-color-menu-item"
                  }
                ]}
              />
              {customColorSwatches?.length > 0 && (
                <Button
                  iconProps={{
                    name: "trash-can"
                  }}
                  size="small"
                  modifier="secondary"
                  onClick={ClearAllCustomColors}
                  dataTestId="account-settings-theme-delete-all-colors-btn"
                >
                  {appstr("Delete all")}
                </Button>
              )}
            </Flex>
            <Grid gap="8px" gridColumns="repeat(10, 1fr)">
              {customColorSwatches?.length > 0 &&
                customColorSwatches.map((item, index) => (
                  <Tooltip
                    key={index}
                    message={item.label}
                    dataTestId={`account-settings-theme-color-swatch-${index}`}
                  >
                    <ColorPickerControl
                      currentColor={item.value}
                      textTags={getLocalizedColorPickerStrings()}
                    />
                  </Tooltip>
                ))}
            </Grid>
          </Flex>
          {showCustomColor === "standard" && (
            <CustomColorPickerModal
              show={showCustomColor}
              onSave={AddCustColor}
              onClose={() => setShowCustomColor(false)}
            />
          )}
          {showCustomColor === "gradient" && (
            <CustomGradientPickerModal
              show={showCustomColor}
              onSave={AddCustColor}
              onClose={() => setShowCustomColor(false)}
            />
          )}
        </Flex>
      </SectionCard>
      {isSupportLogin && <CopyPasteViewsSection />}
    </SectionCardGroup>
  );
};
